import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import TreeView from '@mui/lab/TreeView';
import TreeItem, { useTreeItem } from '@mui/lab/TreeItem';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import CloseIcon from '@mui/icons-material/Close';
import clsx from 'clsx';
import {
  Autocomplete,
  Box,
  Container,
  CssBaseline,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Popover,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
} from 'react-resizable-panels';

import { ConfirmDialog } from '../dialog';
import * as subMatFolderActions from '../../store/subMatFolder';
import * as gclientActions from '../../store/gclient';
import SubMatFolderCreateDialog from './SubMatFolderCreateDialog';
import SubMatFolderSpecDialog from './SubMatFolderSpecDialog';
import SubMatFolderRenameDialog from './SubMatFolderRenameDialog';
import SubMatTestComparisonDialog from './SubMatTestComparisonDialog';
import SubMatFolderPropertyPanel from './SubMatFolderPropertyPanel';
import SubMatFolderExplorerPane, { SubMatWorkProgressOverlay } from './SubMatFolderExplorerPane';
import SubMatFolderExplorerChrome from './SubMatFolderExplorerChrome';
import SubMatFolderIcon, { AdditionalDocBadge } from './SubMatFolderIcon';
import SubMatFolderLinkDialog from './SubMatFolderLinkDialog';
import SubMatFolderContextMenu from './SubMatFolderContextMenu';
import SubMatFolderExpiryDialog from './SubMatFolderExpiryDialog';
import { gclientOptionLabel, renderGclientOption, toGclientSelectOption } from './gclientSelectOption';
import { parseSubMatUploadError } from './SubMatFolderUploadZone';
import { downloadDocument, createPreviewObjectUrl } from './SubMatFolderFileActions';
import { collectDirectoryHandle, downloadCompanyBundle, downloadFolderBundle, uploadCompanyFolder, uploadCompanyZip, uploadFolderBundle, uploadZipBundle } from './subMatFolderBundle';
import DocumentViewerDrawer from '../common/viewer/DocumentViewerDrawer';
import '../../utils/Date';

const PDF_ACCEPT = '.pdf,application/pdf';

/** 트리·탐색기 공통 — 폴더 아이콘·라벨 크기 */
const ENTRY_LABEL_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 0.5,
  fontSize: '0.9375rem',
  lineHeight: 1.45,
};

const TREE_DRAG_THRESHOLD_PX = 6;
const TREE_ZONE_ATTR = 'data-submat-tree';
const EXPLORER_ZONE_ATTR = 'data-submat-explorer';
/** 트리 DnD 시 가장자리 자동 스크롤 */
const TREE_AUTO_SCROLL_EDGE_PX = 48;
const TREE_AUTO_SCROLL_MAX_PX = 22;

const findScrollParent = (el) => {
  let cur = el;
  while (cur && cur !== document.documentElement) {
    if (cur === document.body) break;
    const style = window.getComputedStyle(cur);
    const oy = style.overflowY;
    if ((oy === 'auto' || oy === 'scroll' || oy === 'overlay')
      && cur.scrollHeight > cur.clientHeight + 1) {
      return cur;
    }
    cur = cur.parentElement;
  }
  return el;
};

const treeAutoScrollDelta = (scroller, clientY) => {
  if (!scroller) return 0;
  const rect = scroller.getBoundingClientRect();
  const edge = TREE_AUTO_SCROLL_EDGE_PX;
  if (clientY < rect.top + edge) {
    const t = Math.min(1, Math.max(0, (rect.top + edge - clientY) / edge));
    return -Math.ceil(TREE_AUTO_SCROLL_MAX_PX * t);
  }
  if (clientY > rect.bottom - edge) {
    const t = Math.min(1, Math.max(0, (clientY - (rect.bottom - edge)) / edge));
    return Math.ceil(TREE_AUTO_SCROLL_MAX_PX * t);
  }
  return 0;
};

/** 브라우저 HTML5 드래그와 비슷한 반투명 스냅샷 고스트 */
const FolderDragGhost = ({ folder, x, y }) => {
  if (!folder || x == null || y == null) return null;
  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        left: x + 14,
        top: y + 10,
        zIndex: (t) => t.zIndex.tooltip + 2,
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        pointerEvents: 'none',
        maxWidth: 260,
        px: 0.5,
        py: 0.25,
        fontSize: '0.9375rem',
        lineHeight: 1.45,
        color: 'text.primary',
        bgcolor: 'background.paper',
        opacity: 0.55,
        filter: 'grayscale(0.05)',
        boxShadow: '0 1px 3px rgba(0,0,0,0.18)',
        whiteSpace: 'nowrap',
      }}
    >
      <SubMatFolderIcon folder={folder} />
      <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {folder.name}
      </Box>
    </Box>,
    document.body,
  );
};

/** 범례 물음표. 올리면 열리고, 누르면 고정된다. */
const LegendMarkHelp = ({ label, children }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const pinnedRef = useRef(false);
  const closeTimer = useRef(null);

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  useEffect(() => () => clearCloseTimer(), []);

  const openFrom = (target) => {
    clearCloseTimer();
    setAnchorEl(target);
  };

  const scheduleClose = () => {
    if (pinnedRef.current) return;
    clearCloseTimer();
    closeTimer.current = window.setTimeout(() => setAnchorEl(null), 150);
  };

  const closeAll = () => {
    pinnedRef.current = false;
    clearCloseTimer();
    setAnchorEl(null);
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label={`${label} 설명`}
        onMouseEnter={(e) => openFrom(e.currentTarget)}
        onMouseLeave={scheduleClose}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (pinnedRef.current) closeAll();
          else {
            pinnedRef.current = true;
            openFrom(e.currentTarget);
          }
        }}
        sx={{ p: 0, ml: 0.15 }}
      >
        <HelpOutlineIcon sx={{ fontSize: 14 }} color="action" />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={closeAll}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableAutoFocus
        disableEnforceFocus
        disableRestoreFocus
        sx={{ pointerEvents: 'none' }}
        PaperProps={{
          onMouseEnter: clearCloseTimer,
          onMouseLeave: scheduleClose,
          sx: { pointerEvents: 'auto', p: 1.5, pr: 3.5, maxWidth: 300, position: 'relative' },
        }}
      >
        <IconButton
          size="small"
          aria-label="설명 닫기"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            closeAll();
          }}
          sx={{ position: 'absolute', top: 4, right: 4, p: 0.25 }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
        {children}
      </Popover>
    </>
  );
};

const FolderIconLegend = () => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: 1,
      px: 0.5,
      pt: 0.75,
      mt: 0.5,
      borderTop: 1,
      borderColor: 'divider',
      flexShrink: 0,
    }}
  >
    {[
      { folder: { currentFile: { id: 'legend' } }, label: '사용중' },
      {
        folder: { rootKind: 'GENERAL', spec: { g04GeneralDocuId: 'legend' } },
        label: '문서명/제품명',
        help: (
          <>
            <Typography variant="body2" gutterBottom>
              폴더 이름이 속성에 들어가 있습니다.
            </Typography>
            <Typography variant="body2" gutterBottom>
              문서명 예: 사업자등록증, KS 인증서
            </Typography>
            <Typography variant="body2">
              제품명 예: 로이유리, PVB
            </Typography>
          </>
        ),
      },
      {
        folder: { rootKind: 'TEST', spec: { g04docuGCertificationId: 'legend', productFolderId: 'legend' } },
        label: '규격명',
        help: (
          <>
            <Typography variant="body2" gutterBottom>
              규격 이름이 속성에 들어가 있습니다.
            </Typography>
            <Typography variant="body2">
              같은 제품 아래 규격마다 폴더가 있습니다. 예: 로이유리 아래 6·8, PVB 아래 0.76·1.52
            </Typography>
          </>
        ),
      },
    ].map((item) => (
      <Typography key={item.label} variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
        <SubMatFolderIcon folder={item.folder} />
        {item.label}
        {item.help && (
          <LegendMarkHelp label={item.label}>{item.help}</LegendMarkHelp>
        )}
      </Typography>
    ))}
    <Typography variant="caption" color="text.secondary" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      <AdditionalDocBadge filled />
      <AdditionalDocBadge />
      추가 서류
      <LegendMarkHelp label="추가 서류">
        <Typography variant="body2" gutterBottom>
          채워진 배지는 실물 폴더입니다. 다른 폴더에 붙어 있고, 파일은 여기서 올립니다.
        </Typography>
        <Typography variant="body2">
          테두리 배지는 그 폴더 안에 보이는 줄입니다. 같은 서류를 보여 주며, 보기와 다운로드만 됩니다.
        </Typography>
      </LegendMarkHelp>
    </Typography>
  </Box>
);

const FolderEntryLabel = ({
  folder,
  canDrag = false,
  dragging = false,
  onPointerDown,
  linkTargets = [],
  onOpenAdditional,
}) => (
  <Box
    component="span"
    onPointerDown={canDrag ? onPointerDown : undefined}
    sx={{
      ...ENTRY_LABEL_SX,
      cursor: canDrag ? 'grab' : 'inherit',
      userSelect: 'none',
      width: '100%',
      minWidth: 0,
      overflow: 'hidden',
      opacity: dragging ? 0.35 : 1,
      transition: 'opacity 0.12s ease',
    }}
  >
    <SubMatFolderIcon folder={folder} />
    <Box
      component="span"
      sx={{ flex: '1 1 auto', minWidth: 0, overflow: 'hidden', whiteSpace: 'nowrap' }}
    >
      {folder.name}
    </Box>
    {folder.linkView ? (
      <AdditionalDocBadge
        title={`실물 폴더는 ${folder.name}입니다. 여기서는 보기와 다운로드만 됩니다.`}
      />
    ) : null}
    {!folder.linkView && linkTargets.length > 0 ? (
      <AdditionalDocBadge
        filled
        title={`${linkTargets.map((target) => target.name).filter(Boolean).join(', ')}에 붙어 있습니다.`}
        onClick={() => onOpenAdditional?.(folder)}
      />
    ) : null}
  </Box>
);

/**
 * 행 클릭 = 선택만, 확장/축소는 앞쪽 아이콘에서만.
 * (기본 TreeItemContent는 행 클릭 시 handleExpansion도 호출함)
 */
const SelectOnlyTreeContent = React.forwardRef(function SelectOnlyTreeContent(props, ref) {
  const {
    classes,
    className,
    label,
    nodeId,
    icon: iconProp,
    expansionIcon,
    displayIcon,
    onClick,
    onMouseDown,
    sx,
    ...other
  } = props;

  const {
    disabled,
    expanded,
    selected,
    focused,
    handleExpansion,
    handleSelection,
    preventSelection,
  } = useTreeItem(nodeId);

  const icon = iconProp || expansionIcon || displayIcon;

  const handleMouseDown = (event) => {
    preventSelection(event);
    onMouseDown?.(event);
  };

  const handleClick = (event) => {
    if (event.target?.closest?.('.MuiTreeItem-iconContainer')) {
      handleExpansion(event);
      return;
    }
    handleSelection(event);
    onClick?.(event);
  };

  return (
    <Box
      className={clsx(className, classes.root, {
        [classes.expanded]: expanded,
        [classes.selected]: selected,
        [classes.focused]: focused,
        [classes.disabled]: disabled,
      })}
      onClick={handleClick}
      onMouseDown={handleMouseDown}
      ref={ref}
      sx={sx}
      {...other}
    >
      <div className={classes.iconContainer}>{icon}</div>
      <div className={classes.label}>{label}</div>
    </Box>
  );
});

const renderTree = (nodes, treeFolders, allFolders, links, onContextMenu, dnd, onOpenAdditional) => nodes.map((node) => {
  const children = node.linkView
    ? []
    : subMatFolderActions.childrenWithLinks(treeFolders, allFolders, links, node.id);
  const isRoot = subMatFolderActions.isSystemRootFolder(node);
  const isLink = Boolean(node.linkView);
  const dropAttr = { [subMatFolderActions.DROP_FOLDER_ATTR]: node.id };
  const isDropTarget = dnd?.dropTargetId === node.id && dnd?.draggingId !== node.id;
  const isDragging = dnd?.draggingId === node.id;
  const isCut = Boolean(dnd?.cutFolderIds?.includes(node.id));
  return (
    <TreeItem
      key={node.id}
      nodeId={node.id}
      ContentComponent={SelectOnlyTreeContent}
      label={(
        <FolderEntryLabel
          folder={node}
          canDrag={!isRoot && !isLink}
          dragging={isDragging}
          onPointerDown={!isLink ? (e) => dnd?.onPointerDown?.(e, node) : undefined}
          linkTargets={isLink ? [] : subMatFolderActions.linkTargetsOf(node.id, allFolders, links)}
          onOpenAdditional={onOpenAdditional}
        />
      )}
      ContentProps={{
        'data-submat-ctx': '',
        'data-submat-drop-hover': isDropTarget ? 'true' : undefined,
        ...dropAttr,
        onDragOver: (e) => {
          if (isLink || !subMatFolderActions.isFolderDrag(e.dataTransfer)) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = e.ctrlKey ? 'copy' : 'move';
          dnd?.onHtmlDropTarget?.(node.id);
        },
        onDragLeave: (e) => {
          if (e.currentTarget.contains(e.relatedTarget)) return;
          dnd?.onHtmlDropTarget?.(null);
        },
        onDrop: (e) => {
          e.preventDefault();
          e.stopPropagation();
          dnd?.onHtmlDropTarget?.(null);
          const sourceIds = subMatFolderActions.getFolderDragIds(e.dataTransfer);
          subMatFolderActions.endFolderDrag();
          if (!isLink && sourceIds.length) dnd?.onDrop?.(sourceIds, node, e.ctrlKey);
        },
        onContextMenu: (e) => {
          if (e.target?.closest?.('.MuiTreeItem-iconContainer')) return;
          onContextMenu?.(e, node);
        },
        sx: {
          borderRadius: 0.5,
          transition: 'background-color 0.12s ease, opacity 0.12s ease',
          ...(isDragging ? { opacity: 0.35 } : (isCut ? { opacity: 0.4 } : {})),
        },
      }}
    >
      {children.length > 0 ? renderTree(children, treeFolders, allFolders, links, onContextMenu, dnd, onOpenAdditional) : null}
    </TreeItem>
  );
});

const G04docuSubMatFolderManagement = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const uploadInputRef = useRef(null);
  const bundleInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const companyZipInputRef = useRef(null);
  const companyDirInputRef = useRef(null);
  const bundleParentRef = useRef(null);
  const treeWrapRef = useRef(null);
  /** 트리 포인터 DnD — { sourceId, startX, startY, pointerId, active, ctrlKey } */
  const treePointerDragRef = useRef(null);
  const skipTreeSelectRef = useRef(false);
  const explorerFolderIdsRef = useRef([]);
  const explorerFileInfosRef = useRef([]);
  const [explorerSelectionCount, setExplorerSelectionCount] = useState(0);
  const sessionUser = useSelector((state) => state.session.sessionUser);
  const isAdmin = sessionUser?.type === 'ADMIN';
  const selfId = sessionUser?.gclientId || sessionUser?.id;
  const [gclientOptions, setGclientOptions] = useState([]);
  const [pickedGclientId, setPickedGclientId] = useState('');
  const folders = useSelector((s) => s.subMatFolder.subMatFolders);
  const links = useSelector((s) => s.subMatFolder.subMatFolderLinks) || [];
  const selectedFolder = useSelector((s) => s.subMatFolder.subMatFolder);
  const files = useSelector((s) => s.subMatFolder.subMatFolderFiles);
  const filesFolderId = useSelector((s) => s.subMatFolder.filesFolderId);

  const [loaded, setLoaded] = useState(false);
  const [specOnly, setSpecOnly] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedFileId, setSelectedFileId] = useState(null);
  const [expanded, setExpanded] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [createParent, setCreateParent] = useState(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameFileTarget, setRenameFileTarget] = useState(null); // { folder, file }
  /** { folders: FolderVO[], files: { folderId, file }[] } */
  const [deletePayload, setDeletePayload] = useState(null);
  const [ctxMenu, setCtxMenu] = useState(null);
  /** { mode: 'copy'|'cut', folderIds: string[] } */
  const [clipboard, setClipboard] = useState(null);
  const [renameConflict, setRenameConflict] = useState(null); // { sourceId, targetParentId, mode: move|copy }
  const [errors, setErrors] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activityLabel, setActivityLabel] = useState('');
  const [workProgress, setWorkProgress] = useState(null);
  const workLockRef = useRef(false);
  const [searchQuery, setSearchQuery] = useState('');
  /** 탐색기형 이전/다음 경로 이력 */
  const [navHistory, setNavHistory] = useState({ stack: [], index: -1 });
  const [treeDraggingId, setTreeDraggingId] = useState(null);
  const [treeDropTargetId, setTreeDropTargetId] = useState(null);
  /** 포인터가 중간 탐색기 위에 있을 때만 설정 */
  const [explorerDropTargetId, setExplorerDropTargetId] = useState(null);
  const [treeDragPoint, setTreeDragPoint] = useState(null); // { x, y }
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const previewUrlRef = useRef('');
  const [testComparisonTarget, setTestComparisonTarget] = useState(null); // { folder, reportFile }
  const [expiryTarget, setExpiryTarget] = useState(null); // { folder, file }
  const [specFolder, setSpecFolder] = useState(null);
  const [linkFolder, setLinkFolder] = useState(null);

  // Chrome: MUI TreeItem 포커스 리다이렉트가 HTML5 드래그를 즉시 취소함
  // https://github.com/mui/material-ui/issues/29518
  useEffect(() => {
    const root = treeWrapRef.current;
    if (!root) return undefined;
    const onFocusIn = (e) => {
      if (e.target?.closest?.('.MuiTreeItem-content, .MuiTreeItem-label')) {
        e.stopImmediatePropagation();
      }
    };
    root.addEventListener('focusin', onFocusIn, true);
    return () => root.removeEventListener('focusin', onFocusIn, true);
  }, []);

  const folderMap = useMemo(() => {
    const map = {};
    folders.forEach((f) => { map[f.id] = f; });
    return map;
  }, [folders]);

  const linkNode = useMemo(
    () => subMatFolderActions.linkViewById(selectedId, folders, links),
    [selectedId, folders, links],
  );
  const selected = linkNode
    || (selectedFolder && selectedFolder.id === selectedId ? selectedFolder : null)
    || folderMap[selectedId]
    || null;
  const fileFolderId = selected?.linkView ? selected.sourceFolderId : selected?.id;
  const uploadable = subMatFolderActions.isUploadableFolder(selected);
  const folderFiles = filesFolderId === fileFolderId ? files : [];

  const loadTree = useCallback(async (keepSelectionId, { ensureDefaults = false, silent = false } = {}) => {
    if (!silent) setLoaded(false);
    const data = await dispatch(subMatFolderActions.selectTree({ ensureDefaults }));
    if (keepSelectionId && String(keepSelectionId).startsWith(subMatFolderActions.LINK_NODE_PREFIX)) {
      const linkId = String(keepSelectionId).slice(subMatFolderActions.LINK_NODE_PREFIX.length);
      const link = (data?.subMatFolderLinks || []).find((item) => item.id === linkId);
      if (link) await dispatch(subMatFolderActions.selectFiles(link.sourceFolderId));
    } else if (keepSelectionId) {
      await dispatch(subMatFolderActions.select(keepSelectionId));
    }
    if (!silent) setLoaded(true);
  }, [dispatch]);

  const handlePickGclient = useCallback((option) => {
    if (!option?.id || option.id === pickedGclientId || workLockRef.current) return;
    const own = option.id === selfId;
    subMatFolderActions.setActingGclientId(own ? '' : option.id);
    setPickedGclientId(option.id);
    setSelectedId(null);
    setSelectedFileId(null);
    setSearchQuery('');
    setClipboard(null);
    setExpanded([]);
    setNavHistory({ stack: [], index: -1 });
    loadTree(undefined, { ensureDefaults: own });
  }, [loadTree, pickedGclientId, selfId]);

  useEffect(() => {
    if (!isAdmin) return undefined;
    let alive = true;
    gclientActions.selectValidAllDirect()
      .then((list) => {
        if (!alive) return;
        setGclientOptions((list || []).map((g) => toGclientSelectOption(g)));
        setPickedGclientId((current) => current || selfId || '');
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [isAdmin, selfId]);

  useEffect(() => () => {
    subMatFolderActions.setActingGclientId('');
  }, []);

  const handleRefresh = useCallback(async () => {
    if (workLockRef.current) return;
    const id = selectedId;
    await loadTree(id);
    if (!id) return;
    if (String(id).startsWith(subMatFolderActions.LINK_NODE_PREFIX)) return;
    const folder = folderMap[id] || selectedFolder;
    if (subMatFolderActions.isUploadableFolder(folder)) {
      await dispatch(subMatFolderActions.selectFiles(id));
    }
  }, [dispatch, folderMap, loadTree, selectedFolder, selectedId]);

  useEffect(() => {
    loadTree(undefined, { ensureDefaults: true });
  }, [dispatch, loadTree]);

  useEffect(() => {
    if (folders.length && expanded.length === 0) {
      // 최상위(0)만 확장 → 1레벨 폴더까지 보이게
      setExpanded(folders.filter((f) => f.folderLevel === 0).map((f) => f.id));
    }
  }, [folders, expanded.length]);

  useEffect(() => {
    if (!fileFolderId) return;
    if (uploadable || selected?.linkView) {
      dispatch(subMatFolderActions.selectFiles(fileFolderId));
    }
  }, [fileFolderId, uploadable, selected?.linkView, dispatch]);

  useEffect(() => {
    if (!selected || !uploadable) {
      setSelectedFileId(null);
      return;
    }
    // 폴더 전환 시 이전 선택이 이 폴더에 없으면 해제 (사용중 파일 자동 선택 안 함)
    setSelectedFileId((prev) => {
      if (prev && folderFiles.some((f) => f.id === prev)) return prev;
      return null;
    });
  }, [selected?.id, uploadable, folderFiles]);

  const navigateTo = async (nodeId, { fromHistory = false, pushHistory = true } = {}) => {
    setSelectedId(nodeId);
    setSelectedFileId(null);
    setSearchQuery('');
    setErrors([]);
    const view = subMatFolderActions.linkViewById(nodeId, folders, links);
    if (view) {
      await dispatch(subMatFolderActions.selectFiles(view.sourceFolderId));
    } else if (nodeId) {
      await dispatch(subMatFolderActions.select(nodeId));
    }

    if (pushHistory && !fromHistory && nodeId) {
      setNavHistory((prev) => {
        const stack = prev.stack.slice(0, prev.index + 1);
        if (stack.length && stack[stack.length - 1] === nodeId) {
          return { stack, index: stack.length - 1 };
        }
        const next = [...stack, nodeId];
        return { stack: next, index: next.length - 1 };
      });
    }
  };

  const scrollTreeNodeIntoView = (nodeId) => {
    if (!nodeId || !treeWrapRef.current) return;
    requestAnimationFrame(() => {
      const wrap = treeWrapRef.current;
      const content = wrap?.querySelector(
        `[${subMatFolderActions.DROP_FOLDER_ATTR}="${nodeId}"]`,
      );
      const scroller = wrap?.querySelector('.MuiTreeView-root');
      if (!content || !scroller) return;
      const nodeRect = content.getBoundingClientRect();
      const viewRect = scroller.getBoundingClientRect();
      if (nodeRect.top < viewRect.top) {
        scroller.scrollTop -= viewRect.top - nodeRect.top;
      } else if (nodeRect.bottom > viewRect.bottom) {
        scroller.scrollTop += nodeRect.bottom - viewRect.bottom;
      }
    });
  };

  const goBack = async () => {
    let targetId = null;
    setNavHistory((prev) => {
      if (prev.index <= 0) return prev;
      const nextIndex = prev.index - 1;
      targetId = prev.stack[nextIndex];
      return { ...prev, index: nextIndex };
    });
    if (targetId) await navigateTo(targetId, { fromHistory: true });
  };

  const goForward = async () => {
    let targetId = null;
    setNavHistory((prev) => {
      if (prev.index < 0 || prev.index >= prev.stack.length - 1) return prev;
      const nextIndex = prev.index + 1;
      targetId = prev.stack[nextIndex];
      return { ...prev, index: nextIndex };
    });
    if (targetId) await navigateTo(targetId, { fromHistory: true });
  };

  const goUp = async () => {
    if (!selected?.parentId) return;
    await navigateTo(selected.parentId);
  };

  const canGoBack = navHistory.index > 0;
  const canGoForward = navHistory.index >= 0 && navHistory.index < navHistory.stack.length - 1;
  const canGoUp = Boolean(selected?.parentId);

  const handleSelect = async (event, nodeId) => {
    // 확장/축소 아이콘 클릭은 onNodeToggle만 처리 (선택 시 navigateTo가 expanded를 덮어써 축소가 안 됨)
    if (event?.target?.closest?.('.MuiTreeItem-iconContainer')) {
      return;
    }
    // 포인터 DnD 직후 발생하는 click/select는 무시
    if (skipTreeSelectRef.current) {
      skipTreeSelectRef.current = false;
      return;
    }
    // 키보드 Space/Enter 선택은 onKeyDown에서 중간 탐색기 조회
    if (event?.type === 'keydown' || event?.nativeEvent instanceof KeyboardEvent) {
      return;
    }
    await navigateTo(nodeId);
    scrollTreeNodeIntoView(nodeId);
  };

  /** 키보드 화살표 = 트리 하이라이트만(중간 탐색기 조회 없음). Space에서 조회 */
  const handleNodeFocus = (event, nodeId) => {
    if (!nodeId || treePointerDragRef.current?.active) return;
    setSelectedId(nodeId);
    scrollTreeNodeIntoView(nodeId);
  };

  const handleTreeKeyDown = (event) => {
    if (event.key !== ' ' && event.code !== 'Space') return;
    event.preventDefault();
    if (!selectedId || treePointerDragRef.current?.active) return;
    navigateTo(selectedId);
  };

  const handleRefreshDetail = async (id) => {
    await loadTree(id);
  };

  const pruneFolderIdsForDelete = (ids) => {
    const idSet = new Set(ids);
    return ids.filter((id) => {
      const f = folderMap[id];
      if (!f || f.isSystem) return false;
      let cur = f.parentId ? folderMap[f.parentId] : null;
      while (cur) {
        if (idSet.has(cur.id)) return false;
        cur = cur.parentId ? folderMap[cur.parentId] : null;
      }
      return true;
    });
  };

  const openDeleteConfirm = (folderIds, fileInfos) => {
    if (workLockRef.current) return;
    const prunedIds = pruneFolderIdsForDelete([...new Set((folderIds || []).filter(Boolean))]);
    const folderTargets = prunedIds.map((id) => folderMap[id]).filter(Boolean);

    const subtree = new Set();
    prunedIds.forEach((id) => {
      subMatFolderActions.collectDescendantIds(folders, id).forEach((x) => subtree.add(x));
    });

    const selectedFileIds = new Set((fileInfos || []).map((x) => x.file?.id).filter(Boolean));
    const fileTargets = (fileInfos || []).filter(({ folderId, file }) => {
      if (!file?.id || !folderId) return false;
      if (subtree.has(folderId)) return false;
      if (file.fileRole === 'TEST_COMPARISON' && file.pairedFileId && selectedFileIds.has(file.pairedFileId)) {
        return false;
      }
      return true;
    });

    if (!folderTargets.length && !fileTargets.length) return;
    setDeletePayload({ folders: folderTargets, files: fileTargets });
  };

  const requestDeleteSelection = () => {
    openDeleteConfirm(explorerFolderIdsRef.current || [], explorerFileInfosRef.current || []);
  };

  const requestDeleteFromToolbar = () => {
    const folderIds = explorerFolderIdsRef.current || [];
    const fileInfos = explorerFileInfosRef.current || [];
    if (folderIds.length || fileInfos.length) {
      openDeleteConfirm(folderIds, fileInfos);
      return;
    }
    if (selected && !selected.isSystem) {
      openDeleteConfirm([selected.id], []);
    }
  };

  const handleConfirmDelete = async () => {
    if (workLockRef.current) return;
    const payload = deletePayload;
    if (!payload) return;
    const folderTargets = payload.folders || [];
    const fileTargets = payload.files || [];
    const deletedFolderIds = new Set();
    const accErrors = [];
    const total = folderTargets.length + fileTargets.length + 1;
    let done = 0;
    workLockRef.current = true;

    const showDelete = (name, completed) => {
      setWorkProgress({ verb: '삭제', name, done: completed, total });
    };

    try {
    for (const folder of folderTargets) {
      showDelete(folder.name || '', done);
      try {
        await dispatch(subMatFolderActions.remove(folder.id));
        subMatFolderActions.collectDescendantIds(folders, folder.id).forEach((d) => deletedFolderIds.add(d));
      } catch (err) {
        const sr = err?.serverResponse;
        if (sr?.errors?.length) accErrors.push(...sr.errors);
        else if (sr?.message) accErrors.push(sr.message);
        else accErrors.push(err?.message || '폴더 삭제에 실패했습니다.');
      }
      done += 1;
    }

    let keepFileId = selectedFileId;
    for (const item of fileTargets) {
      showDelete(item.file?.fileName || '', done);
      if (!deletedFolderIds.has(item.folderId)) {
        try {
          await dispatch(subMatFolderActions.removeFile(item.folderId, item.file.id));
          if (keepFileId === item.file.id) keepFileId = null;
        } catch (err) {
          const msgs = await parseSubMatUploadError(err);
          accErrors.push(...msgs);
        }
      }
      done += 1;
    }

    const openId = selected?.id || selectedId;
    let nextFolderId = openId || null;
    const openedFolderDeleted = Boolean(openId && deletedFolderIds.has(openId));
    if (openedFolderDeleted) {
      let parentId = folderMap[openId]?.parentId || null;
      while (parentId && deletedFolderIds.has(parentId)) {
        parentId = folderMap[parentId]?.parentId || null;
      }
      nextFolderId = parentId;
      keepFileId = null;
    } else if (selectedId && deletedFolderIds.has(selectedId)) {
      nextFolderId = openId;
    }

    setSelectedId(nextFolderId);
    setSelectedFileId(keepFileId);
    if (openedFolderDeleted) setSearchQuery('');

    if (deletedFolderIds.size) {
      setNavHistory((prev) => {
        const stack = prev.stack.filter((id) => !deletedFolderIds.has(id));
        if (openedFolderDeleted && nextFolderId && !stack.includes(nextFolderId)) {
          stack.push(nextFolderId);
        }
        if (!stack.length) {
          return nextFolderId ? { stack: [nextFolderId], index: 0 } : { stack: [], index: -1 };
        }
        if (openedFolderDeleted && nextFolderId) {
          return { stack, index: Math.max(stack.lastIndexOf(nextFolderId), 0) };
        }
        const keptUntilCurrent = prev.stack
          .slice(0, prev.index + 1)
          .filter((id) => !deletedFolderIds.has(id));
        const index = Math.min(Math.max(keptUntilCurrent.length - 1, 0), stack.length - 1);
        return { stack, index };
      });
    }

    if (clipboard?.folderIds?.some((id) => deletedFolderIds.has(id))) {
      setClipboard(null);
    }

    if (accErrors.length) setErrors(accErrors);
    showDelete('목록 새로고침', done);
    await loadTree(nextFolderId || undefined, { silent: true });
    if (nextFolderId) {
      await dispatch(subMatFolderActions.selectFiles(nextFolderId));
      if (openedFolderDeleted) scrollTreeNodeIntoView(nextFolderId);
    }
    } catch (err) {
      setErrors((prev) => [...prev, err?.message || '삭제 후 목록을 다시 읽지 못했습니다.']);
    } finally {
      workLockRef.current = false;
      setWorkProgress(null);
      setDeletePayload(null);
    }
  };

  const handleFileUpload = async (file) => {
    if (workLockRef.current) return;
    if (!selected || !uploadable || !file) return;
    if (file.type && file.type !== 'application/pdf' && !file.name?.toLowerCase().endsWith('.pdf')) {
      setErrors(['PDF 파일만 업로드할 수 있습니다.']);
      return;
    }
    setActivityLabel('업로드 중…');
    setUploading(true);
    try {
      const uploaded = await dispatch(subMatFolderActions.uploadFile(selected.id, file));
      await dispatch(subMatFolderActions.selectFiles(selected.id));
      await handleRefreshDetail(selected.id);
      if (uploaded?.id) setSelectedFileId(uploaded.id);
    } catch (err) {
      const msgs = await parseSubMatUploadError(err);
      setErrors(msgs);
    } finally {
      setUploading(false);
      setActivityLabel('');
    }
  };

  const handleDownloadBundle = async (folder) => {
    if (!folder?.id || workLockRef.current) return;
    setActivityLabel('받는 중…');
    setUploading(true);
    try {
      await downloadFolderBundle(folder);
    } catch (err) {
      setErrors([err?.message || '폴더를 받지 못했습니다.']);
    } finally {
      setUploading(false);
      setActivityLabel('');
    }
  };

  const finishBundleUpload = async (parentFolder, upload) => {
    if (workLockRef.current) return;
    setActivityLabel('업로드 중…');
    setUploading(true);
    setErrors([]);
    try {
      const { warnings } = await upload();
      await loadTree(parentFolder?.id, { silent: true });
      if (parentFolder && subMatFolderActions.isUploadableFolder(parentFolder)) {
        await dispatch(subMatFolderActions.selectFiles(parentFolder.id));
      }
      if (warnings?.length) setErrors(warnings);
    } catch (err) {
      setErrors([err?.message || '폴더를 올리지 못했습니다.']);
    } finally {
      setUploading(false);
      setActivityLabel('');
    }
  };

  const handleUploadBundle = async (fileList) => {
    const parentFolder = bundleParentRef.current;
    if (!parentFolder?.id || !fileList?.length) return;
    await finishBundleUpload(parentFolder, () => uploadFolderBundle({
      dispatch,
      parentFolder,
      fileList,
    }));
  };

  const handleUploadOsFolder = (folder) => {
    if (!folder?.id) return;
    if (typeof window.showDirectoryPicker !== 'function') {
      bundleParentRef.current = folder;
      bundleInputRef.current?.click();
      return;
    }
    window.showDirectoryPicker({ mode: 'read' })
      .then((handle) => finishBundleUpload(folder, async () => {
        const collected = await collectDirectoryHandle(handle);
        return uploadFolderBundle({
          dispatch,
          parentFolder: folder,
          dirs: collected.dirs,
          warnings: collected.warnings,
        });
      }))
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setErrors([err?.message || '폴더를 열지 못했습니다.']);
      });
  };

  const handleUploadZip = async (file) => {
    const parentFolder = bundleParentRef.current;
    if (!parentFolder?.id || !file) return;
    await finishBundleUpload(parentFolder, () => uploadZipBundle(parentFolder, file));
  };

  const handleDownloadAll = async () => {
    if (workLockRef.current) return;
    setActivityLabel('받는 중…');
    setUploading(true);
    try {
      await downloadCompanyBundle();
    } catch (err) {
      setErrors([err?.message || '전체를 받지 못했습니다.']);
    } finally {
      setUploading(false);
      setActivityLabel('');
    }
  };

  const handleUploadAllFolder = () => {
    if (typeof window.showDirectoryPicker !== 'function') {
      companyDirInputRef.current?.click();
      return;
    }
    window.showDirectoryPicker({ mode: 'read' })
      .then((handle) => finishBundleUpload(selected, async () => {
        const collected = await collectDirectoryHandle(handle);
        return uploadCompanyFolder({
          dispatch,
          dirs: collected.dirs,
          rootFolders: folders,
          warnings: collected.warnings,
        });
      }))
      .catch((err) => {
        if (err?.name === 'AbortError') return;
        setErrors([err?.message || '폴더를 열지 못했습니다.']);
      });
  };

  const handleUploadAllFolderList = async (fileList) => {
    if (!fileList?.length) return;
    await finishBundleUpload(selected, () => uploadCompanyFolder({
      dispatch,
      fileList,
      rootFolders: folders,
    }));
  };

  const handleUploadAllZipFile = async (file) => {
    if (!file) return;
    await finishBundleUpload(selected, () => uploadCompanyZip(file));
  };

  const openContextMenu = (e, payload) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ mouseX: e.clientX, mouseY: e.clientY, ...payload });
  };

  const handleTreeContextMenu = (e, folder) => {
    openContextMenu(e, { kind: 'folder', folder });
  };

  const handleExplorerContextMenu = (e, payload) => {
    openContextMenu(e, payload);
  };

  const openCreateDialog = (parent) => {
    setCreateParent(parent || selected);
    setCreateOpen(true);
  };

  const openRenameDialog = (folder) => {
    setRenameFileTarget(null);
    setRenameTarget(folder);
    setRenameOpen(true);
  };

  const openRenameFileDialog = (folder, file) => {
    setRenameTarget(null);
    setRenameFileTarget({ folder: folder || selected, file });
    setRenameOpen(true);
  };

  const requestDeleteFolder = (folder) => {
    const clicked = folder || selected;
    if (!clicked) return;
    const selectedIds = explorerFolderIdsRef.current || [];
    const selectedFiles = explorerFileInfosRef.current || [];
    const inSel = selectedIds.includes(clicked.id);
    if (inSel && (selectedIds.length > 1 || selectedFiles.length)) {
      openDeleteConfirm(selectedIds, selectedFiles);
      return;
    }
    openDeleteConfirm([clicked.id], []);
  };

  const emptyingRootChildLabels = (targets) => {
    const byParent = new Map();
    (targets || []).forEach((f) => {
      if (!f?.parentId) return;
      const parent = folderMap[f.parentId];
      if (!parent || parent.folderLevel !== 0) return;
      if (!byParent.has(f.parentId)) byParent.set(f.parentId, []);
      byParent.get(f.parentId).push(f);
    });
    const labels = [];
    byParent.forEach((list, pid) => {
      const siblings = folders.filter((f) => f.parentId === pid);
      if (!siblings.length || list.length !== siblings.length) return;
      const rootLabel = subMatFolderActions.ROOT_KIND_LABEL[folderMap[pid]?.rootKind]
        || folderMap[pid]?.name
        || '상위';
      labels.push(rootLabel === '일반문서' ? '기본 일반문서 폴더' : `${rootLabel}1`);
    });
    return labels;
  };

  const deleteConfirmTitle = () => {
    if (!deletePayload) return '삭제';
    const hasFolders = Boolean(deletePayload.folders?.length);
    const hasFiles = Boolean(deletePayload.files?.length);
    if (hasFolders && hasFiles) return '항목 삭제';
    if (hasFiles) return '문서 삭제';
    return '폴더 삭제';
  };

  const deleteConfirmMessage = () => {
    if (!deletePayload) return '';
    const fs = deletePayload.folders || [];
    const fls = deletePayload.files || [];
    const lines = [];

    if (fs.length === 1 && !fls.length) {
      const target = fs[0];
      const hasChildren = folders.some((f) => f.parentId === target.id);
      lines.push((target.currentFile || hasChildren)
        ? '선택한 폴더와 하위 폴더·업로드된 파일(물리 문서 포함)·시험성과대비표·만료/선행 속성이 모두 삭제됩니다. 템플릿 목차에 연결된 항목도 제거됩니다. 계속하시겠습니까?'
        : '선택한 폴더를 삭제하시겠습니까?');
    } else {
      if (fs.length) {
        lines.push(`선택한 폴더 ${fs.length}개와 그 하위 폴더·업로드된 파일(물리 문서 포함)·시험성과대비표·만료/선행 속성·템플릿 목차 연결이 모두 삭제됩니다.`);
      }
      if (fls.length === 1 && !fs.length) {
        const t = fls[0].file;
        lines.push(t.comparisonFile
          ? `「${t.fileName}」을(를) 삭제하시겠습니까?\n연결된 시험성과대비표「${t.comparisonFile.fileName || '시험성과대비표'}」도 함께 삭제됩니다. 물리 파일과 만료·선행 속성도 함께 삭제됩니다.`
          : `「${t.fileName}」을(를) 삭제하시겠습니까? 물리 파일과 만료·선행 속성도 함께 삭제됩니다.`);
      } else if (fls.length) {
        lines.push(`선택한 문서 ${fls.length}개를 삭제합니다. 연결된 시험성과대비표·물리 파일·만료/선행 속성도 함께 삭제됩니다.`);
      }
      if (fs.length && fls.length) lines.push('계속하시겠습니까?');
      else if (fs.length > 1) lines.push('계속하시겠습니까?');
    }

    const emptied = emptyingRootChildLabels(fs);
    if (emptied.length) {
      const names = [...new Set(emptied)].map((n) => `「${n}」`).join(', ');
      lines.push(`\n이 아래 폴더를 모두 지우면, 다음에 서류관리에 들어갈 때 ${names}가 다시 만들어질 수 있습니다.`);
    }
    return lines.join('\n');
  };

  const relocateFolder = async (sourceId, targetParentId, mode, allowRename = false, { reload = true } = {}) => {
    try {
      if (mode === 'copy') {
        await dispatch(subMatFolderActions.copyFolder(sourceId, targetParentId, allowRename));
      } else {
        await dispatch(subMatFolderActions.moveFolder(sourceId, targetParentId, allowRename));
      }
      if (reload) await loadTree(selectedId, { silent: true });
      return true;
    } catch (err) {
      if (!allowRename && subMatFolderActions.isDuplicateNameError(err)) {
        setRenameConflict({ sourceId, targetParentId, mode });
        return false;
      }
      setErrors([err?.serverResponse?.message || err.message || '폴더 작업에 실패했습니다.']);
      return false;
    }
  };

  const runTrackedRelocate = async (ids, targetParentId, mode, allowRename) => {
    const verb = mode === 'copy' ? '복사' : '이동';
    const total = ids.length + 1;
    workLockRef.current = true;
    let moved = false;
    let halted = false;
    try {
      for (let i = 0; i < ids.length; i += 1) {
        const source = folderMap[ids[i]];
        setWorkProgress({ verb, name: source?.name || '', done: i, total });
        const ok = await relocateFolder(ids[i], targetParentId, mode, allowRename, { reload: false });
        if (ok) moved = true;
        else if (!allowRename) {
          halted = true;
          break;
        }
      }
      if (moved) {
        setWorkProgress({ verb, name: '목록 새로고침', done: ids.length, total });
        await loadTree(selectedId, { silent: true });
      }
      return { moved, halted };
    } catch (err) {
      setErrors([err?.message || '목록을 다시 읽지 못했습니다.']);
      return { moved, halted };
    } finally {
      workLockRef.current = false;
      setWorkProgress(null);
    }
  };

  const handleFolderDrop = async (sourceIdOrIds, targetFolder, isCopy) => {
    if (workLockRef.current || uploading) return false;
    if (!targetFolder || targetFolder.linkView) return;
    const sourceIds = [...new Set(
      (Array.isArray(sourceIdOrIds) ? sourceIdOrIds : [sourceIdOrIds]).filter(Boolean),
    )];
    if (!sourceIds.length) return;

    const mode = isCopy ? 'copy' : 'move';
    const multi = sourceIds.length > 1;
    const idSet = new Set(sourceIds);
    // 부모·자식이 함께 선택된 경우 자식은 제외(부모 이동에 포함됨)
    const pruned = sourceIds.filter((id) => {
      let cur = folderMap[id];
      cur = cur?.parentId ? folderMap[cur.parentId] : null;
      while (cur) {
        if (idSet.has(cur.id)) return false;
        cur = cur.parentId ? folderMap[cur.parentId] : null;
      }
      return true;
    });

    const validIds = [];
    for (const sourceId of pruned) {
      if (sourceId === targetFolder.id) continue;
      const source = folderMap[sourceId];
      if (!source || subMatFolderActions.isSystemRootFolder(source)) continue;
      if (!isCopy && source.parentId === targetFolder.id) continue;
      if (!subMatFolderActions.canAddChildFolder(targetFolder, folders)) {
        setErrors(['이 폴더 아래에는 폴더를 둘 수 없습니다.']);
        return false;
      }
      const intoProduct = subMatFolderActions.isNamedProductFolder(targetFolder)
        || subMatFolderActions.ancestorNamedProduct(targetFolder, folders);
      if (intoProduct && (
        subMatFolderActions.isNamedProductFolder(source)
        || subMatFolderActions.isProcessSpecFolder(source)
      )) {
        setErrors(['제품 아래에는 두께를 고르는 폴더만 둡니다.']);
        return false;
      }
      const descendants = subMatFolderActions.collectDescendantIds(folders, sourceId);
      if (descendants.has(targetFolder.id)) {
        setErrors(['하위 폴더 안으로는 이동·복사할 수 없습니다.']);
        return false;
      }
      validIds.push(sourceId);
    }
    if (!validIds.length) return true;

    const { moved, halted } = await runTrackedRelocate(validIds, targetFolder.id, mode, multi);
    if (moved) {
      setExpanded((prev) => (
        prev.includes(targetFolder.id) ? prev : [...prev, targetFolder.id]
      ));
    }
    if (moved && mode === 'move' && clipboard?.mode === 'cut') {
      const cutSet = new Set(clipboard.folderIds || []);
      if (sourceIds.some((id) => cutSet.has(id))) {
        setClipboard(null);
      }
    }
    if (halted) return false;
    return true;
  };

  const handleConfirmRenameConflict = async () => {
    if (!renameConflict || workLockRef.current) return;
    const { sourceId, targetParentId, mode } = renameConflict;
    setRenameConflict(null);
    const { moved } = await runTrackedRelocate([sourceId], targetParentId, mode, true);
    if (moved && targetParentId) {
      setExpanded((prev) => (prev.includes(targetParentId) ? prev : [...prev, targetParentId]));
    }
    if (moved && mode === 'move' && clipboard?.mode === 'cut' && clipboard.folderIds?.includes(sourceId)) {
      const next = clipboard.folderIds.filter((id) => id !== sourceId);
      setClipboard(next.length ? { ...clipboard, folderIds: next } : null);
    }
  };

  const handleFolderDropRef = useRef(handleFolderDrop);
  handleFolderDropRef.current = handleFolderDrop;
  const folderMapRef = useRef(folderMap);
  folderMapRef.current = folderMap;

  const handleTreePointerDown = (e, node) => {
    if (e.button !== 0) return;
    if (workLockRef.current) return;
    if (subMatFolderActions.isSystemRootFolder(node)) return;
    if (e.target?.closest?.('.MuiTreeItem-iconContainer')) return;
    treePointerDragRef.current = {
      sourceId: node.id,
      startX: e.clientX,
      startY: e.clientY,
      pointerId: e.pointerId,
      active: false,
      ctrlKey: e.ctrlKey,
    };
  };

  // 트리 → 트리/탐색기: MUI HTML5 DnD가 Chrome에서 깨져 포인터 DnD 사용
  useEffect(() => {
    let autoScrollRaf = null;
    let lastPointerY = 0;

    const clearUi = () => {
      setTreeDraggingId(null);
      setTreeDropTargetId(null);
      setExplorerDropTargetId(null);
      setTreeDragPoint(null);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    const stopAutoScroll = () => {
      if (autoScrollRaf != null) {
        cancelAnimationFrame(autoScrollRaf);
        autoScrollRaf = null;
      }
    };

    const runAutoScroll = () => {
      autoScrollRaf = null;
      if (!treePointerDragRef.current?.active) return;
      const root = treeWrapRef.current;
      if (!root) return;
      const scroller = findScrollParent(root);
      const dy = treeAutoScrollDelta(scroller, lastPointerY);
      if (!dy) return;
      const prev = scroller.scrollTop;
      scroller.scrollTop = prev + dy;
      if (scroller.scrollTop === prev) return;

      const x = treePointerDragRef.current.lastX ?? 0;
      const overId = subMatFolderActions.findDropFolderIdAtPoint(x, lastPointerY);
      const sourceId = treePointerDragRef.current.sourceId;
      const validOver = overId && overId !== sourceId ? overId : null;
      setTreeDropTargetId(validOver);
      setExplorerDropTargetId(null);
      autoScrollRaf = requestAnimationFrame(runAutoScroll);
    };

    const ensureAutoScroll = () => {
      const root = treeWrapRef.current;
      if (!root || !treePointerDragRef.current?.active) {
        stopAutoScroll();
        return;
      }
      const scroller = findScrollParent(root);
      if (treeAutoScrollDelta(scroller, lastPointerY)) {
        if (autoScrollRaf == null) autoScrollRaf = requestAnimationFrame(runAutoScroll);
      } else {
        stopAutoScroll();
      }
    };

    const resolveZone = (clientX, clientY) => {
      const el = document.elementFromPoint(clientX, clientY);
      if (el?.closest?.(`[${EXPLORER_ZONE_ATTR}]`)) return 'explorer';
      if (el?.closest?.(`[${TREE_ZONE_ATTR}]`)) return 'tree';
      // 트리 영역 위·아래로 포인터가 나가도 가로 범위 안이면 자동 스크롤용으로 트리 취급
      const root = treeWrapRef.current;
      if (root && treePointerDragRef.current?.active) {
        const scroller = findScrollParent(root);
        const rect = scroller.getBoundingClientRect();
        if (clientX >= rect.left && clientX <= rect.right
          && (clientY < rect.top || clientY > rect.bottom)) {
          return 'tree';
        }
      }
      return null;
    };

    const onMove = (e) => {
      const st = treePointerDragRef.current;
      if (!st) return;
      const dist = Math.hypot(e.clientX - st.startX, e.clientY - st.startY);
      if (!st.active && dist >= TREE_DRAG_THRESHOLD_PX) {
        st.active = true;
        skipTreeSelectRef.current = true;
        subMatFolderActions.beginFolderDrag(st.sourceId);
        setTreeDraggingId(st.sourceId);
        document.body.style.cursor = 'grabbing';
        document.body.style.userSelect = 'none';
      }
      if (!st.active) return;
      st.ctrlKey = e.ctrlKey;
      st.lastX = e.clientX;
      lastPointerY = e.clientY;
      setTreeDragPoint({ x: e.clientX, y: e.clientY });

      const zone = resolveZone(e.clientX, e.clientY);
      const overId = subMatFolderActions.findDropFolderIdAtPoint(e.clientX, e.clientY);
      const validOver = overId && overId !== st.sourceId ? overId : null;

      if (zone === 'tree') {
        setTreeDropTargetId(validOver);
        setExplorerDropTargetId(null);
        ensureAutoScroll();
      } else if (zone === 'explorer') {
        stopAutoScroll();
        setTreeDropTargetId(null);
        setExplorerDropTargetId(validOver || 'pane');
      } else {
        stopAutoScroll();
        setTreeDropTargetId(null);
        setExplorerDropTargetId(null);
      }
    };

    const onUp = (e) => {
      const st = treePointerDragRef.current;
      treePointerDragRef.current = null;
      stopAutoScroll();
      if (!st) return;
      const wasActive = st.active;
      const sourceId = st.sourceId;
      const ctrlKey = e.ctrlKey || st.ctrlKey;
      const overId = wasActive
        ? subMatFolderActions.findDropFolderIdAtPoint(e.clientX, e.clientY)
        : null;
      subMatFolderActions.endFolderDrag();
      clearUi();
      if (!wasActive || !overId || overId === sourceId) {
        if (!wasActive) skipTreeSelectRef.current = false;
        return;
      }
      const target = folderMapRef.current[overId];
      if (target) handleFolderDropRef.current(sourceId, target, ctrlKey);
    };

    const onKey = (e) => {
      if (e.key !== 'Escape' || !treePointerDragRef.current) return;
      treePointerDragRef.current = null;
      stopAutoScroll();
      subMatFolderActions.endFolderDrag();
      clearUi();
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    window.addEventListener('keydown', onKey);
    return () => {
      stopAutoScroll();
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const clipboardIds = () => (clipboard?.folderIds || []).filter(Boolean);

  const setClipboardFromClickedFolder = (mode, clickedFolder) => {
    if (!clickedFolder || subMatFolderActions.isSystemRootFolder(clickedFolder)) return;
    const selected = explorerFolderIdsRef.current || [];
    let ids = [clickedFolder.id];
    if (selected.includes(clickedFolder.id) && selected.length > 1) {
      ids = selected;
    }
    ids = [...new Set(ids)].filter((id) => {
      const f = folderMap[id];
      return f && !subMatFolderActions.isSystemRootFolder(f);
    });
    if (!ids.length) return;
    setClipboard({ mode, folderIds: ids });
  };

  const handlePaste = async (targetFolder) => {
    const ids = clipboardIds();
    const wasCut = clipboard?.mode === 'cut';
    if (!ids.length || !targetFolder) return;
    const ok = await handleFolderDrop(ids, targetFolder, clipboard?.mode === 'copy');
    if (wasCut && ok !== false) {
      setClipboard(null);
    }
  };

  const revokePreviewUrl = useCallback(() => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = '';
    }
    setPreviewUrl('');
  }, []);

  useEffect(() => () => {
    if (previewUrlRef.current) {
      window.URL.revokeObjectURL(previewUrlRef.current);
    }
  }, []);

  const closePreview = useCallback(() => {
    setPreviewOpen(false);
    revokePreviewUrl();
  }, [revokePreviewUrl]);

  const handleViewFile = async (file) => {
    if (!file?.documentPath) return;
    try {
      const url = await createPreviewObjectUrl(file.documentPath);
      revokePreviewUrl();
      previewUrlRef.current = url;
      setPreviewUrl(url);
      setPreviewOpen(true);
    } catch (err) {
      setErrors([err.message || '파일을 열 수 없습니다.']);
    }
  };

  const handleDownloadFile = async (file) => {
    if (!file?.documentPath) return;
    try {
      await downloadDocument(file.documentPath, file.fileName);
    } catch (err) {
      setErrors([err.message || '다운로드에 실패했습니다.']);
    }
  };

  const handleSetCurrentFromMenu = async (file) => {
    if (!selected || !file) return;
    try {
      await dispatch(subMatFolderActions.setCurrentFile(selected.id, file.id));
      await dispatch(subMatFolderActions.selectFiles(selected.id));
      await handleRefreshDetail(selected.id);
      setSelectedFileId(file.id);
    } catch (err) {
      const msgs = await parseSubMatUploadError(err);
      setErrors(msgs);
    }
  };

  const handleRequestDeleteFile = (file) => {
    if (!file) return;
    const selectedFiles = explorerFileInfosRef.current || [];
    const selectedIds = explorerFolderIdsRef.current || [];
    const inSel = selectedFiles.some((x) => x.file.id === file.id);
    if (inSel && (selectedFiles.length > 1 || selectedIds.length)) {
      openDeleteConfirm(selectedIds, selectedFiles);
      return;
    }
    const folderId = file.folderId || selected?.id;
    if (!folderId) return;
    openDeleteConfirm([], [{ folderId, file }]);
  };

  const ctxFolder = ctxMenu?.folder;
  const ctxCanAddChild = !ctxFolder?.linkView && subMatFolderActions.canAddChildFolder(ctxFolder || selected, folders);
  const ctxCanRename = subMatFolderActions.canRenameFolder(ctxFolder);

  const handleDetachLink = async (view) => {
    if (!view?.sourceFolderId || !view?.parentId) return;
    const next = (links || [])
      .filter((link) => link.sourceFolderId === view.sourceFolderId && link.targetFolderId !== view.parentId)
      .map((link) => link.targetFolderId);
    try {
      await dispatch(subMatFolderActions.replaceFolderLinks(view.sourceFolderId, next));
      if (selectedId === view.id) {
        setSelectedId(view.parentId);
        await dispatch(subMatFolderActions.select(view.parentId));
      }
    } catch (err) {
      setErrors([err?.serverResponse?.message || err.message || '추가 서류를 빼지 못했습니다.']);
    }
  };

  const visibleFolders = useMemo(
    () => (specOnly ? subMatFolderActions.foldersForSpecView(folders) : folders),
    [specOnly, folders],
  );

  const folderTreeView = (
    <Box ref={treeWrapRef} {...{ [TREE_ZONE_ATTR]: '' }} sx={{ flex: 1, minHeight: 0, height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {loaded && folders.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          아직 폴더가 없습니다.
        </Typography>
      )}
      <TreeView
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
        expanded={expanded}
        onNodeToggle={(_e, ids) => setExpanded(ids)}
        selected={selectedId || ''}
        onNodeSelect={handleSelect}
        onNodeFocus={handleNodeFocus}
        onKeyDown={handleTreeKeyDown}
        sx={{
          flex: 1,
          minHeight: { xs: 200, md: 0 },
          overflowX: 'hidden',
          overflowY: 'auto',
          '& .MuiTreeItem-content': {
            py: 0.35,
            minHeight: 32,
            boxSizing: 'border-box',
            overflow: 'hidden',
            border: '1px solid transparent',
            borderRadius: '4px',
            mb: '2px',
          },
          '& .MuiTreeItem-content.Mui-selected': {
            borderColor: 'primary.main',
          },
          '& .MuiTreeItem-label': {
            fontSize: '0.9375rem',
            p: 0,
            minWidth: 0,
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          },
          // 키보드 포커스도 선택색과 동일(회색 focus 대신)
          '& .MuiTreeItem-content.Mui-focused': {
            borderColor: 'primary.main',
            backgroundColor: (t) => alpha(
              t.palette.primary.main,
              t.palette.action.selectedOpacity,
            ),
          },
          '& .MuiTreeItem-content.Mui-focused:hover': {
            backgroundColor: (t) => alpha(
              t.palette.primary.main,
              t.palette.action.selectedOpacity + t.palette.action.hoverOpacity,
            ),
          },
          // 드롭 대상 = 선택색(Mui-selected)과 동일
          '& .MuiTreeItem-content[data-submat-drop-hover="true"]': {
            backgroundColor: (t) => alpha(
              t.palette.primary.main,
              t.palette.action.selectedOpacity,
            ),
            '&:hover': {
              backgroundColor: (t) => alpha(
                t.palette.primary.main,
                t.palette.action.selectedOpacity + t.palette.action.hoverOpacity,
              ),
            },
          },
        }}
      >
        {renderTree(
          subMatFolderActions.childrenWithLinks(visibleFolders, folders, links, null),
          visibleFolders,
          folders,
          links,
          handleTreeContextMenu,
          {
            onDrop: handleFolderDrop,
            onPointerDown: handleTreePointerDown,
            draggingId: treeDraggingId,
            dropTargetId: treeDropTargetId,
            onHtmlDropTarget: setTreeDropTargetId,
            cutFolderIds: clipboard?.mode === 'cut' ? (clipboard.folderIds || []) : [],
          },
          (folder) => setLinkFolder(folder),
        )}
      </TreeView>
      <FolderIconLegend />
      <SubMatWorkProgressOverlay progress={workProgress} />
    </Box>
  );

  const explorerPane = (chrome) => (
    <SubMatFolderExplorerPane
      folder={selected}
      folderMap={folderMap}
      folders={visibleFolders}
      allFolders={folders}
      links={links}
      files={files}
      filesFolderId={filesFolderId}
      selectedFileId={selectedFileId}
      uploading={uploading}
      activityLabel={activityLabel}
      busy={Boolean(workProgress)}
      workProgress={workProgress}
      chrome={chrome}
      searchQuery={chrome ? undefined : searchQuery}
      onSearchQueryChange={chrome ? undefined : setSearchQuery}
      onNavigate={navigateTo}
      onCreateFolder={() => openCreateDialog(selected)}
      onDeleteFolder={requestDeleteFromToolbar}
      onDeleteSelection={requestDeleteSelection}
      onUploadFile={handleFileUpload}
      onSelectFile={setSelectedFileId}
      onContextMenu={handleExplorerContextMenu}
      onPreviewFile={handleViewFile}
      onError={(msg) => setErrors([msg])}
      onFolderDrop={handleFolderDrop}
      cutFolderIds={clipboard?.mode === 'cut' ? (clipboard.folderIds || []) : []}
      selectedFolderIdsRef={explorerFolderIdsRef}
      selectedFileInfosRef={explorerFileInfosRef}
      onSelectionChange={setExplorerSelectionCount}
      externalDropTargetId={explorerDropTargetId}
      canGoBack={canGoBack}
      canGoForward={canGoForward}
      canGoUp={canGoUp}
      onGoBack={goBack}
      onGoForward={goForward}
      onGoUp={goUp}
      onRefresh={handleRefresh}
      onOpenAdditional={(folder) => setLinkFolder(folder)}
      specOnly={specOnly}
      onSpecOnlyChange={setSpecOnly}
    />
  );

  const historyPanel = (
    <SubMatFolderPropertyPanel
      folder={selected}
      folders={folders}
      files={files}
      filesFolderId={filesFolderId}
      selectedFileId={selectedFileId}
      onSelectFile={setSelectedFileId}
      onSetCurrentFile={handleSetCurrentFromMenu}
      onContextMenu={handleExplorerContextMenu}
      onPreviewFile={handleViewFile}
      onError={(msg) => setErrors([msg])}
    />
  );

  const chromeBar = (
    <SubMatFolderExplorerChrome
      folder={selected}
      folderMap={folderMap}
      uploading={uploading}
      activityLabel={activityLabel}
      busy={Boolean(workProgress)}
      searchQuery={searchQuery}
      onSearchQueryChange={setSearchQuery}
      onNavigate={navigateTo}
      onCreateFolder={() => openCreateDialog(selected)}
      onDeleteFolder={requestDeleteFromToolbar}
      hasDeletableSelection={explorerSelectionCount > 0}
      onUploadFile={handleFileUpload}
      canGoBack={canGoBack}
      canGoForward={canGoForward}
      canGoUp={canGoUp}
      onGoBack={goBack}
      onGoForward={goForward}
      onGoUp={goUp}
      onRefresh={handleRefresh}
      specOnly={specOnly}
      onSpecOnlyChange={setSpecOnly}
      onDownloadAll={handleDownloadAll}
      onUploadAllZip={() => companyZipInputRef.current?.click()}
      onUploadAllFolder={handleUploadAllFolder}
    />
  );

  return (
    <Container component="main" maxWidth="false">
      <CssBaseline />
      {treeDraggingId && treeDragPoint && (
        <FolderDragGhost
          folder={folderMap[treeDraggingId]}
          x={treeDragPoint.x}
          y={treeDragPoint.y}
        />
      )}
      <input
        ref={uploadInputRef}
        type="file"
        hidden
        accept={PDF_ACCEPT}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleFileUpload(file);
          e.target.value = '';
        }}
      />
      <input
        ref={bundleInputRef}
        type="file"
        hidden
        multiple
        webkitdirectory=""
        directory=""
        onChange={(e) => {
          const list = e.target.files;
          if (list?.length) handleUploadBundle(list);
          e.target.value = '';
        }}
      />
      <input
        ref={zipInputRef}
        type="file"
        hidden
        accept=".zip,application/zip"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUploadZip(file);
          e.target.value = '';
        }}
      />
      <input
        ref={companyZipInputRef}
        type="file"
        hidden
        accept=".zip,application/zip"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) handleUploadAllZipFile(file);
          e.target.value = '';
        }}
      />
      <input
        ref={companyDirInputRef}
        type="file"
        hidden
        multiple
        webkitdirectory=""
        directory=""
        onChange={(e) => {
          const list = e.target.files;
          if (list?.length) handleUploadAllFolderList(list);
          e.target.value = '';
        }}
      />
      {isAdmin && (
        <Box sx={{ mt: 2, mb: 1, maxWidth: 640 }}>
          <Autocomplete
            size="small"
            options={gclientOptions}
            value={gclientOptions.find((option) => option.id === pickedGclientId) || null}
            onChange={(_event, option) => { if (option) handlePickGclient(option); }}
            getOptionLabel={gclientOptionLabel}
            renderOption={renderGclientOption}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            disableClearable
            disabled={Boolean(workProgress) || uploading}
            renderInput={(params) => <TextField {...params} label="거래선명" />}
          />
        </Box>
      )}
      {errors.length > 0 && (
        <Box sx={{ color: 'error.main', mb: 1, mt: 2 }}>{errors.map((e, i) => <div key={i}>{e}</div>)}</Box>
      )}
      {!loaded && <LinearProgress sx={{ mb: 1, mt: errors.length ? 0 : 2 }} />}

      {isMobile ? (
        <Box sx={{ mt: (errors.length || !loaded) ? 0 : 2 }}>
          <Paper variant="outlined" sx={{ mb: 2, overflow: 'hidden' }}>
            {chromeBar}
          </Paper>
          <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ p: 1, minHeight: 280, display: 'flex', flexDirection: 'column' }}>
              {folderTreeView}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ minHeight: 360, overflow: 'hidden' }}>
              {explorerPane(false)}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <Paper variant="outlined" sx={{ minHeight: 280, overflow: 'hidden' }}>
              {historyPanel}
            </Paper>
          </Grid>
          </Grid>
        </Box>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            mt: (errors.length || !loaded) ? 0 : 2,
            height: 'calc(100vh - 160px)',
            minHeight: 520,
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {chromeBar}
          <PanelGroup direction="horizontal" style={{ flex: 1, minHeight: 0 }}>
            <Panel defaultSize={28} minSize={16}>
              <Box sx={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 1 }}>
                {folderTreeView}
              </Box>
            </Panel>
            <PanelResizeHandle style={{ width: 6, background: 'rgba(0,0,0,0.08)' }} />
            <Panel defaultSize={72} minSize={40}>
              <Box sx={{ height: '100%', borderLeft: 1, borderColor: 'divider', minWidth: 0 }}>
                <PanelGroup direction="vertical" style={{ height: '100%' }}>
                  <Panel defaultSize={48} minSize={28}>
                    <Box sx={{ height: '100%', overflow: 'hidden' }}>
                      {explorerPane(false)}
                    </Box>
                  </Panel>
                  <PanelResizeHandle style={{ height: 6, background: 'rgba(0,0,0,0.08)' }} />
                  <Panel defaultSize={52} minSize={22}>
                    <Box sx={{ height: '100%', overflow: 'hidden', borderTop: 1, borderColor: 'divider' }}>
                      {historyPanel}
                    </Box>
                  </Panel>
                </PanelGroup>
              </Box>
            </Panel>
          </PanelGroup>
        </Paper>
      )}

      <SubMatFolderContextMenu
        state={ctxMenu}
        onClose={() => setCtxMenu(null)}
        onOpenFolder={(f) => navigateTo(f.id)}
        onRenameFolder={(f) => openRenameDialog(f)}
        onFolderSpec={(f) => setSpecFolder(f)}
        onAttachAdditional={(f) => setLinkFolder(f.linkView ? folderMap[f.sourceFolderId] : f)}
        onDetachAdditional={handleDetachLink}
        onRenameFile={(f, file) => openRenameFileDialog(f, file)}
        onCreateChildFolder={(f) => openCreateDialog(f)}
        onDeleteFolder={(f) => requestDeleteFolder(f)}
        onCopyFolder={(f) => setClipboardFromClickedFolder('copy', f)}
        onCutFolder={(f) => setClipboardFromClickedFolder('cut', f)}
        onPasteFolder={(f) => handlePaste(f)}
        canPaste={Boolean(clipboard?.folderIds?.length)}
        onCreateTestComparison={(f, reportFile) => setTestComparisonTarget({ folder: f, reportFile })}
        onUpload={() => uploadInputRef.current?.click()}
        onDownloadBundle={handleDownloadBundle}
        onUploadBundle={handleUploadOsFolder}
        onUploadZip={(folder) => {
          bundleParentRef.current = folder;
          zipInputRef.current?.click();
        }}
        onViewFile={handleViewFile}
        onDownloadFile={handleDownloadFile}
        onSetExpiryFile={(f, file) => setExpiryTarget({ folder: f, file })}
        onSetCurrentFile={handleSetCurrentFromMenu}
        onDeleteFile={handleRequestDeleteFile}
        canAddChild={ctxCanAddChild}
        canRenameFolder={ctxCanRename}
        canDeleteFolder={Boolean(ctxFolder && !ctxFolder.isSystem && !ctxFolder.linkView)}
        canCutCopy={Boolean(ctxFolder && !ctxFolder.linkView && !subMatFolderActions.isSystemRootFolder(ctxFolder))}
        uploadable={subMatFolderActions.isUploadableFolder(ctxFolder || selected)}
        fileReadOnly={Boolean(ctxFolder?.linkView)}
      />

      <SubMatFolderSpecDialog
        open={Boolean(specFolder)}
        folder={specFolder}
        folders={folders}
        linkTargetNames={specFolder
          ? subMatFolderActions.linkTargetsOf(specFolder.id, folders, links).map((target) => target.name)
          : []}
        onOpenLinks={() => setLinkFolder(specFolder)}
        onClose={() => setSpecFolder(null)}
        onError={(msg) => setErrors([msg])}
      />

      <SubMatFolderLinkDialog
        open={Boolean(linkFolder)}
        folder={linkFolder}
        folders={folders}
        links={links}
        onClose={() => setLinkFolder(null)}
        onError={(msg) => setErrors([msg])}
      />

      <SubMatTestComparisonDialog
        open={Boolean(testComparisonTarget)}
        folder={testComparisonTarget?.folder || null}
        reportFile={testComparisonTarget?.reportFile || null}
        onClose={() => setTestComparisonTarget(null)}
        onSaved={(folderId) => {
          loadTree(folderId);
          dispatch(subMatFolderActions.selectFiles(folderId));
        }}
      />

      <SubMatFolderExpiryDialog
        open={Boolean(expiryTarget?.file)}
        file={expiryTarget?.file || null}
        folderId={expiryTarget?.folder?.id || expiryTarget?.file?.folderId}
        onClose={() => setExpiryTarget(null)}
        onSaved={() => {
          const fid = expiryTarget?.folder?.id || expiryTarget?.file?.folderId || selectedId;
          if (fid) {
            dispatch(subMatFolderActions.selectFiles(fid));
            handleRefreshDetail(fid);
          }
        }}
      />

      <SubMatFolderCreateDialog
        open={createOpen}
        onClose={() => { setCreateOpen(false); setCreateParent(null); }}
        parentFolder={createParent || selected}
        onCreated={(created) => {
          const parentId = created?.parentId || createParent?.id || selected?.id;
          if (!parentId) return;
          setExpanded((prev) => (prev.includes(parentId) ? prev : [...prev, parentId]));
        }}
      />

      <SubMatFolderRenameDialog
        open={renameOpen}
        folder={renameTarget}
        file={renameFileTarget?.file || null}
        folderId={renameFileTarget?.folder?.id || renameFileTarget?.file?.folderId || null}
        onClose={() => {
          setRenameOpen(false);
          setRenameTarget(null);
          setRenameFileTarget(null);
        }}
        onRenamed={(id) => {
          if (renameFileTarget) {
            const fid = id || renameFileTarget.folder?.id || selectedId;
            if (fid) {
              dispatch(subMatFolderActions.selectFiles(fid));
              handleRefreshDetail(fid);
            }
          } else {
            // 중간 탐색기에서 자식 폴더 이름을 바꿔도 현재 보고 있는 폴더에 남긴다
            loadTree(selectedId || id);
          }
        }}
      />

      <ConfirmDialog
        removeId={[
          ...(deletePayload?.folders || []).map((f) => f.id),
          ...(deletePayload?.files || []).map((x) => x.file.id),
        ].join(',')}
        title={deleteConfirmTitle()}
        open={Boolean(deletePayload)}
        setOpen={(open) => { if (!open) setDeletePayload(null); }}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeletePayload(null)}
      >
        <Box sx={{ whiteSpace: 'pre-wrap' }}>{deleteConfirmMessage()}</Box>
      </ConfirmDialog>

      <ConfirmDialog
        removeId={renameConflict ? `${renameConflict.sourceId}->${renameConflict.targetParentId}` : null}
        title="같은 이름 폴더"
        open={Boolean(renameConflict)}
        setOpen={(open) => { if (!open) setRenameConflict(null); }}
        onConfirm={handleConfirmRenameConflict}
        onCancel={() => setRenameConflict(null)}
      >
        같은 이름의 폴더가 있습니다. 이름을 바꿔서(예: 이름 (2)) 계속할까요?
      </ConfirmDialog>

      <DocumentViewerDrawer
        key={previewUrl || 'closed'}
        open={previewOpen}
        onClose={closePreview}
        url={previewUrl}
      />
    </Container>
  );
};

export default G04docuSubMatFolderManagement;
